const asText = (value) => (typeof value === "string" ? value.trim() : "");

const parseObjectField = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const pickFirstValue = (list, keys = ["value", "email", "phone", "address"]) => {
  if (!Array.isArray(list)) return "";
  for (const item of list) {
    if (typeof item === "string" && item.trim()) return item.trim();
    if (item && typeof item === "object") {
      for (const key of keys) {
        const raw = item[key];
        if (typeof raw === "string" && raw.trim()) return raw.trim();
      }
    }
  }
  return "";
};

const pickFirstEntry = (list, keys = ["value", "email", "phone", "address"]) => {
  if (!Array.isArray(list)) return null;
  for (const item of list) {
    if (typeof item === "string" && item.trim()) return { value: item.trim() };
    if (item && typeof item === "object") {
      for (const key of keys) {
        const raw = item[key];
        if (typeof raw === "string" && raw.trim()) {
          return {
            value: raw.trim(),
            source: asText(item.source),
            confidence: asText(item.confidence),
          };
        }
      }
    }
  }
  return null;
};

export const toContactCard = (contact) => {
  const identity = parseObjectField(contact?.identity);
  const reach = parseObjectField(contact?.reach);
  const data = parseObjectField(contact?.data);
  const sourceMeta = parseObjectField(data?.source);
  const person = identity?.person || {};
  const company = identity?.company || {};

  const firstName = asText(person.firstName) || asText(identity.firstName);
  const lastName = asText(person.lastName) || asText(identity.lastName);
  const personTitle = asText(person.title);
  const companyTitle = asText(company.type);
  const companyName = asText(company.name) || asText(identity.companyName);

  const phoneEntry =
    pickFirstEntry(reach?.phones, ["value", "phone", "number"]) ||
    pickFirstEntry(identity?.phones, ["value", "phone", "number"]);
  const emailEntry =
    pickFirstEntry(reach?.emails, ["value", "email", "address"]) ||
    pickFirstEntry(identity?.emails, ["value", "email", "address"]);
  const propertyOriginalSource =
    asText(sourceMeta?.origin).toLowerCase() === "propertyasset"
      ? asText(sourceMeta?.originalSource)
      : "";
  const withPropertySourceFallback = (entry) => {
    const sourceText = asText(entry?.source) || propertyOriginalSource;
    const confidenceText = asText(entry?.confidence);
    return {
      source: sourceText,
      confidence:
        propertyOriginalSource && confidenceText.toLowerCase() === "unverified"
          ? ""
          : confidenceText,
    };
  };
  const phoneMeta = withPropertySourceFallback(phoneEntry);
  const emailMeta = withPropertySourceFallback(emailEntry);

  // Pull primary address from reach.addresses[]; format US-style.
  const addressEntry = (() => {
    const list = Array.isArray(reach?.addresses) ? reach.addresses : [];
    if (list.length === 0) return null;
    // prefer mailing/primary/home, else first
    const preferred = list.find((e) => /mailing|primary|home/i.test(asText(e?.type))) || list[0];
    return preferred && typeof preferred === "object" ? preferred : null;
  })();
  const addressFormatted = (() => {
    if (!addressEntry) return "";
    const a = addressEntry.address || addressEntry;
    const line1 = asText(a.line1 || a.street);
    const line2 = asText(a.line2);
    const unit = asText(a.suiteOrUnit || a.unit);
    const city = asText(a.city);
    const state = asText(a.state);
    const zip = asText(a.zipCode || a.zip);
    const zip4 = asText(a.zip4);
    const fullZip = [zip, zip4].filter(Boolean).join("-");
    const stateZip = [state, fullZip].filter(Boolean).join(" ");
    const cityLine = [city, stateZip].filter(Boolean).join(", ");
    const streetLine = [line1, unit ? `#${unit}` : "", line2].filter(Boolean).join(" ");
    return [streetLine, cityLine].filter(Boolean).join(", ");
  })();

  return {
    id: contact?.id || "",
    firstName,
    lastName,
    personTitle,
    companyTitle,
    company: companyName,
    phone: phoneEntry?.value || "",
    phoneSource: phoneMeta.source,
    phoneConfidence: phoneMeta.confidence,
    email: emailEntry?.value || "",
    emailSource: emailMeta.source,
    emailConfidence: emailMeta.confidence,
    source: asText(contact?.data?.source ?? contact?.source),
    confidence: asText(contact?.data?.confidence ?? contact?.confidence),
    status: asText(contact?.status),
    state: asText(contact?.state),
    category: asText(contact?.category),
    domain: asText(contact?.domain),
    address: addressFormatted,
    sourceLabels: toSourceLabels(contact, sourceMeta),
    propertyListIds: Array.isArray(contact?.propertyListIds) ? contact.propertyListIds : [],
    propertyCount: Array.isArray(contact?.propertyListIds) ? contact.propertyListIds.length : 0,
    updatedAt: contact?.updatedAt ?? contact?.createdAt ?? null,
  };
};

const SOURCE_LABEL_MAP = {
  john_filemaker_2026_03_10: "FileMaker",
  salesforce_aptotude_5818: "Aptotude",
};

const shortLabelFromRaw = (raw) => {
  const text = asText(raw).trim();
  if (!text) return "";
  // Common prefix patterns: "Excel: <details>", "CSV: ...", "Crexi - ...".
  // Take the first segment before colon / dash / slash so the chip stays tidy
  // while the full string remains in tooltip.
  const head = text.split(/[:|\-/]/)[0].trim();
  const candidate = head || text;
  return candidate.length > 24 ? `${candidate.slice(0, 21)}…` : candidate;
};

const toSourceLabels = (contact, sourceMeta) => {
  const collected = new Set();
  const add = (raw) => {
    const text = asText(raw);
    const key = text.toLowerCase();
    if (!key) return;
    if (SOURCE_LABEL_MAP[key]) {
      collected.add(SOURCE_LABEL_MAP[key]);
      return;
    }
    if (key.includes("filemaker")) { collected.add("FileMaker"); return; }
    if (key.includes("aptotude") || key.includes("salesforce")) { collected.add("Aptotude"); return; }
    const short = shortLabelFromRaw(text);
    if (short) collected.add(short);
  };
  const sourcesArr = Array.isArray(contact?.data?.sources) ? contact.data.sources : (Array.isArray(contact?.sources) ? contact.sources : null);
  if (sourcesArr) sourcesArr.forEach(add);
  add(contact?.data?.source ?? contact?.source);
  add(sourceMeta?.originalSource);
  return Array.from(collected);
};
